import React, { useState, useEffect } from 'react';
import { 
  WifiIcon, 
  SignalSlashIcon, 
  ArrowPathIcon, 
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  TrashIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { useOffline, useOfflineStatus } from '@/hooks/useOffline';
import { useBackgroundSync } from '@/hooks/useBackgroundSync';
import { SyncModal, useSyncModal } from '@/components/SyncModal';
import { offlineStorage } from '@/services/offlineStorage';
import { syncService } from '@/services/syncService';

const OfflineManagementPage: React.FC = () => {
  const { isOnline, isOfflineMode, toggleOfflineMode } = useOffline();
  const { statusMessage, statusColor, canSync, pendingChanges, lastSync } = useOfflineStatus();
  const { status: backgroundStatus, start, stop, getDiagnostics, cleanup } = useBackgroundSync();
  const { isOpen, openSyncModal, closeSyncModal, handleSyncComplete } = useSyncModal();
  
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isTogglingMode, setIsTogglingMode] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);

  // Charger les diagnostics
  const loadDiagnostics = async () => {
    setIsLoading(true);
    try {
      const diag = await getDiagnostics();
      setDiagnostics(diag);
      console.log('✅ Diagnostics chargés:', diag);
    } catch (error) {
      console.error('❌ Erreur chargement diagnostics:', error);
      // En cas d'erreur, créer un objet de diagnostic par défaut
      setDiagnostics({
        storageSize: 0,
        pendingItems: [],
        lastError: error instanceof Error ? error.message : 'Erreur inconnue'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnostics();
  }, []);

  const handleSyncClick = async () => {
    setIsSyncing(true);
    try {
      console.log('🔄 Ouverture modal de synchronisation...');
      openSyncModal();
    } catch (error) {
      console.error('❌ Erreur ouverture modal sync:', error);
      // Fallback: synchronisation directe si la modal échoue
      try {
        console.log('🔄 Tentative de synchronisation directe...');
        await syncService.syncData();
        await loadDiagnostics();
      } catch (syncError) {
        console.error('❌ Erreur synchronisation directe:', syncError);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleToggleMode = async () => {
    setIsTogglingMode(true);
    try {
      await toggleOfflineMode();
    } catch (error) {
      console.error('Erreur basculement mode:', error);
    } finally {
      setIsTogglingMode(false);
    }
  };

  const handleForceSync = async () => {
    setIsSyncing(true);
    try {
      console.log('🔄 Synchronisation forcée en cours...');
      const result = await syncService.forceFullSync();
      console.log('✅ Synchronisation forcée terminée:', result);
      await loadDiagnostics();
    } catch (error) {
      console.error('❌ Erreur synchronisation forcée:', error);
      // Essayer une synchronisation normale en fallback
      try {
        console.log('🔄 Tentative de synchronisation normale...');
        await syncService.syncData();
        await loadDiagnostics();
      } catch (fallbackError) {
        console.error('❌ Erreur synchronisation fallback:', fallbackError);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCleanup = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir nettoyer les données anciennes ?')) {
      setIsCleaning(true);
      try {
        console.log('🧹 Nettoyage des données anciennes...');
        await cleanup();
        console.log('✅ Nettoyage terminé');
        await loadDiagnostics();
      } catch (error) {
        console.error('❌ Erreur nettoyage:', error);
      } finally {
        setIsCleaning(false);
      }
    }
  };

  const handleClearAllData = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer TOUTES les données offline ? Cette action est irréversible.')) {
      setIsCleaning(true);
      try {
        console.log('🗑️ Suppression de toutes les données offline...');
        await offlineStorage.clearAllData();
        console.log('✅ Suppression terminée');
        await loadDiagnostics();
      } catch (error) {
        console.error('❌ Erreur suppression données:', error);
      } finally {
        setIsCleaning(false);
      }
    }
  };

  const getStatusIcon = () => {
    if (isOnline && !isOfflineMode) {
      return <WifiIcon className="h-6 w-6 text-green-500" />;
    }
    if (isOfflineMode) {
      return <SignalSlashIcon className="h-6 w-6 text-orange-500" />;
    }
    if (!isOnline) {
      return <SignalSlashIcon className="h-6 w-6 text-red-500" />;
    }
    return <InformationCircleIcon className="h-6 w-6 text-gray-500" />;
  };

  const getStatusBgColor = () => {
    switch (statusColor) {
      case 'green': return 'bg-green-50 border-green-200';
      case 'orange': return 'bg-orange-50 border-orange-200';
      case 'red': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* En-tête */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion Hors Ligne</h1>
            <p className="text-gray-600 mt-1">Gérez le mode offline et la synchronisation des données</p>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className="text-sm font-medium text-gray-700">
              {isOnline && !isOfflineMode ? 'En ligne' : 'Hors ligne'}
            </span>
          </div>
        </div>
      </div>

      {/* Statut de connexion */}
      <div className={`border rounded-lg p-6 ${getStatusBgColor()}`}>
        <div className="flex items-center space-x-3 mb-4">
          {getStatusIcon()}
          <div>
            <h2 className="text-lg font-semibold">État de la connexion</h2>
            <p className="text-sm text-gray-600">{statusMessage}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-2">
            <WifiIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm">Internet: {isOnline ? 'Connecté' : 'Déconnecté'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <SignalSlashIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm">Mode: {isOfflineMode ? 'Offline manuel' : 'Automatique'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm">Dernière sync: {lastSync ? lastSync.toLocaleString() : 'Jamais'}</span>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-3 mt-6">
          <button
            onClick={handleToggleMode}
            disabled={isTogglingMode}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              isOfflineMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-orange-600 text-white hover:bg-orange-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isTogglingMode ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <SignalSlashIcon className="h-4 w-4" />
            )}
            <span>{isOfflineMode ? 'Passer en ligne' : 'Passer offline'}</span>
          </button>

          {canSync && (
            <button
              onClick={handleSyncClick}
              disabled={isSyncing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSyncing ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowPathIcon className="h-4 w-4" />
              )}
              <span>Synchroniser</span>
            </button>
          )}
        </div>
      </div>

      {/* Statistiques de synchronisation */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CloudArrowUpIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Changements en attente</p>
              <p className="text-2xl font-semibold text-gray-900">{pendingChanges}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sync réussies</p>
              <p className="text-2xl font-semibold text-gray-900">{backgroundStatus.syncCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Erreurs</p>
              <p className="text-2xl font-semibold text-gray-900">{backgroundStatus.errorCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-gray-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Prochaine sync</p>
              <p className="text-sm font-semibold text-gray-900">
                {backgroundStatus.nextSync ? backgroundStatus.nextSync.toLocaleTimeString() : 'Non programmée'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Synchronisation automatique */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Synchronisation automatique</h2>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              backgroundStatus.isRunning ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {backgroundStatus.isRunning ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Dernière synchronisation</p>
            <p className="font-medium">
              {backgroundStatus.lastSync ? backgroundStatus.lastSync.toLocaleString() : 'Jamais'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Prochaine synchronisation</p>
            <p className="font-medium">
              {backgroundStatus.nextSync ? backgroundStatus.nextSync.toLocaleString() : 'Non programmée'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => backgroundStatus.isRunning ? stop() : start()}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              backgroundStatus.isRunning 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <CogIcon className="h-4 w-4" />
            <span>{backgroundStatus.isRunning ? 'Arrêter' : 'Démarrer'}</span>
          </button>

          <button
            onClick={handleForceSync}
            disabled={isSyncing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSyncing ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowPathIcon className="h-4 w-4" />
            )}
            <span>Sync forcée</span>
          </button>
        </div>
      </div>

      {/* Données locales */}
      {(diagnostics || isLoading) && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Données locales</h2>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
            >
              <CogIcon className="h-4 w-4" />
              <span>{showAdvanced ? 'Masquer' : 'Afficher'} les détails</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Taille du stockage</p>
              <p className="text-lg font-semibold text-gray-900">
                {diagnostics ? (diagnostics.storageSize / 1024 / 1024).toFixed(2) : '0.00'} MB
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Éléments en attente</p>
              <p className="text-lg font-semibold text-gray-900">
                {diagnostics ? diagnostics.pendingItems.length : 0}
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Dernière erreur</p>
              <p className="text-sm text-gray-600">
                {backgroundStatus.lastError || (diagnostics?.lastError) || 'Aucune'}
              </p>
            </div>
          </div>

          {showAdvanced && (
            <div className="mt-6">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Détails techniques</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <pre className="text-xs text-gray-600 overflow-auto">
                  {diagnostics ? JSON.stringify(diagnostics, null, 2) : 'Chargement des diagnostics...'}
                </pre>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={loadDiagnostics}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>

            <button
              onClick={handleCleanup}
              disabled={isCleaning}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCleaning ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <TrashIcon className="h-4 w-4" />
              )}
              <span>Nettoyer</span>
            </button>

            <button
              onClick={handleClearAllData}
              disabled={isCleaning}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCleaning ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <TrashIcon className="h-4 w-4" />
              )}
              <span>Supprimer tout</span>
            </button>
          </div>
        </div>
      )}

      {/* Modal de synchronisation */}
      <SyncModal
        isOpen={isOpen}
        onClose={closeSyncModal}
        onSyncComplete={handleSyncComplete}
      />
    </div>
  );
};

export default OfflineManagementPage;
